import React from "react";
import { Button, Tooltip, Icon, notification } from "antd";
import moment from "moment";
import Request from "../../request";

class MiniTimer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            running: false,
            hours: 0,
            minutes: 0,
            seconds: 0,
            intervalId: null,
            latitude: 0,
            longitude: 0,
            activeUser: {},
            companyDetails: {},
        };
    }

    componentDidMount() {
        this.getActiveUser();
        this.getLoginData();
        this.fetchLocation();
    }

    componentWillUnmount() {
        if (this.state.intervalId) clearInterval(this.state.intervalId);
    }

    // ✅ Fetch active user & company settings
    getActiveUser = async () => {
        let activeUser = JSON.parse(localStorage.getItem("user")) || {};
        let companyDetails = await Request.getCompanySetting({
            businessId: activeUser.businessId,
        });
        this.setState({ activeUser, companyDetails });
    };

    // ✅ Fetch last login data & start timer if needed
    getLoginData = async () => {
        try {
            let response = await Request.getLoginTime();
            if (response.success && response.lastLogin) {
                const lastLogin = response.lastLogin;
                if (!lastLogin.logoutAt) {
                    let loginTime = moment(lastLogin.loginAt, 'HH:mm:ss');
                    let currentTime = moment();
                    // calculate diff as total seconds
                    let diffSeconds = currentTime.diff(loginTime, 'seconds');

                    // convert to hours, minutes, seconds
                    let hours = Math.floor(diffSeconds / 3600);
                    let minutes = Math.floor((diffSeconds % 3600) / 60);
                    let seconds = diffSeconds % 60;

                    this.setState({
                        running: true,
                        hours,
                        minutes,
                        seconds,
                    });

                    // start interval safely
                    if (!this.state.intervalId) {
                        const intervalId = setInterval(this.updateTimer, 1000);
                        this.setState({ intervalId });
                    }
                }
            }
        } catch (err) {
            console.error("Error fetching login data", err);
        }
    };


    fetchLocation = () => {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                this.setState({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            },
            (err) => console.warn("Location error:", err)
        );
    };

    updateTimer = () => {
        let { hours, minutes, seconds } = this.state;
        seconds += 1;
        if (seconds > 59) {
            minutes += 1;
            seconds = 0;
        }
        if (minutes > 59) {
            hours += 1;
            minutes = 0;
        }
        this.setState({ hours, minutes, seconds });
    };

    checkLoginLogoutTime = async () => {
        const { shiftTiming } = this.state.activeUser;
        if (!shiftTiming) return {};
        const timing = shiftTiming.split(" - ");
        const shiftStartTime = moment(timing[0], "HH:mm:ss A");
        const shiftEndTime = moment(timing[1], "HH:mm:ss A");
        return { shiftStartTime, shiftEndTime };
    };

    handleStart = async () => {
        this.setState({ loading: true });
        const { activeUser, companyDetails } = this.state;
        let lateLoginReason = "";

        if (activeUser.shiftTiming && companyDetails.promptForEarlyLoginLogout) {
            const timing = await this.checkLoginLogoutTime();
            const newShiftStart = timing.shiftStartTime.add(5, "minutes");
            if (moment() > newShiftStart) {
                lateLoginReason = prompt("Enter Reason For Late Login");
                if (!lateLoginReason) {
                    notification.error({ message: "Enter Reason For Late Login First" });
                    this.setState({ loading: false });
                    return;
                }
            }
        }

        const location = {
            latitude: this.state.latitude,
            longitude: this.state.longitude,
        };

        const response = await Request.startTimer({
            location,
            lateLoginReason,
            loginTime: moment(),
        });

        if (response.error) {
            notification.error({ message: response.message });
            this.setState({ loading: false });
            return;
        }

        const intervalId = setInterval(this.updateTimer, 1000);
        this.setState({
            running: true,
            loading: false,
            intervalId,
            hours: 0,
            minutes: 0,
            seconds: 0,
        });
    };

    handleStop = async () => {
        this.setState({ loading: true });
        const { activeUser, companyDetails } = this.state;
        let earlyLogoutReason = "";

        if (activeUser.shiftTiming && companyDetails.promptForEarlyLoginLogout) {
            const timing = await this.checkLoginLogoutTime();
            if (moment() < timing.shiftEndTime) {
                earlyLogoutReason = prompt("Enter Reason For Early Logout");
                if (!earlyLogoutReason) {
                    notification.error({ message: "Enter Reason For Early Logout First" });
                    this.setState({ loading: false });
                    return;
                }
            }
        }

        const response = await Request.stopTimer({
            earlyLogoutReason,
            logoutTime: moment(),
        });

        if (response.error) {
            notification.error({ message: response.message });
            this.setState({ loading: false });
            return;
        }

        clearInterval(this.state.intervalId);
        this.setState({
            running: false,
            loading: false,
            hours: 0,
            minutes: 0,
            seconds: 0,
            intervalId: null,
        });
    };

    render() {
        const { hours, minutes, seconds, running, loading } = this.state;
        const dotColor = running ? "#52c41a" : "#f5222d";
        const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#fff",
                    borderRadius: "25px",
                    padding: "6px 12px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                    gap: "10px",
                }}
            >
                <div style={{ position: "relative" }}>
                    <Icon type="clock-circle" style={{ fontSize: 18, color: "#1890ff" }} />
                    <span
                        style={{
                            position: "absolute",
                            top: "-3px",
                            right: "-3px",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: dotColor,
                            animation: "blink 1s infinite",
                        }}
                    />
                </div>

                <Tooltip title="Worked Time">
                    <span
                        style={{
                            fontWeight: 500,
                            minWidth: "75px",
                            fontSize: "14px",
                            color: "#333",
                        }}
                    >
                        {formattedTime}
                    </span>
                </Tooltip>

                {running ? (
                    <Button
                        type="danger"
                        icon="pause"
                        size="small"
                        loading={loading}
                        onClick={this.handleStop}
                        style={{
                            borderRadius: "20px",
                            padding: "0 14px",
                            fontWeight: 500,
                        }}
                    >
                        Stop
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        icon="caret-right"
                        size="small"
                        loading={loading}
                        onClick={this.handleStart}
                        style={{
                            borderRadius: "20px",
                            padding: "0 14px",
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                            fontWeight: 500,
                        }}
                    >
                        Start
                    </Button>
                )}

                <style>
                    {`
            @keyframes blink {
              0%, 50%, 100% { opacity: 1; }
              25%, 75% { opacity: 0.3; }
            }
          `}
                </style>
            </div>
        );
    }
}

export default MiniTimer;
